import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getEmployeeDetails, toggleEmployeeAutoAssign } from '../api';
import { SkeletonText, SideDrawer, UserIcon, Switch } from '@/components';
import { formatName } from '@/common/utils';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';
import { InlineTagSelector } from '../crm/components/InlineTagSelector';

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-[#868686]">{label}</span>
      <span className="text-sm font-semibold text-[#1F2241] text-right break-words">
        {value === undefined || value === null || value === '' ? '-' : value}
      </span>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div className="bg-[#F5F5F5] rounded-[16px] p-4 sm:p-5 flex flex-col gap-3">
      <h3 className="text-[18px] font-medium text-[#50AB3D]">{title}</h3>
      <div className="bg-white rounded-[12px] p-4 flex flex-col gap-3.5">
        {children}
      </div>
    </div>
  );
}

export function EmployeeDetailsModal({
  isOpen,
  onClose,
  employeeId,
  companyId,
  onEdit,
}) {
  const queryClient = useQueryClient();
  const { can } = usePermissions();

  const { data: employee, isLoading } = useQuery({
    queryKey: ['employeeDetails', companyId, employeeId],
    queryFn: () => getEmployeeDetails(companyId, employeeId),
    enabled: isOpen && !!companyId && !!employeeId,
  });

  const toggleAutoAssignMutation = useMutation({
    mutationFn: (payload) =>
      toggleEmployeeAutoAssign({ companyId, employeeId, payload }),
    onSuccess: () => {
      toast.success('Auto-assign status updated');
      queryClient.invalidateQueries(['employeeDetails', companyId, employeeId]);
      queryClient.invalidateQueries(['companyEmployees', companyId]);
    },
    onError: () => {
      toast.error('Failed to update auto-assign status');
    },
  });

  const handleToggleAutoAssign = (checked) => {
    toggleAutoAssignMutation.mutate({
      is_assignment_enabled: checked,
      is_auto_assign_enabled: checked,
      auto_assign_enabled: checked,
    });
  };

  const roleName =
    typeof employee?.role === 'object'
      ? employee?.role?.name
      : employee?.role || 'No Role Assigned';

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      width="md"
      className="employee-details-drawer"
      showClose={false}
      header={
        <div className="flex items-center justify-between w-full gap-3">
          <div className="flex items-center gap-3 min-w-0 pr-2">
            <div className="w-9 h-9 rounded-full bg-surface-mint flex items-center justify-center shrink-0">
              <UserIcon className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              {isLoading ? (
                <>
                  <SkeletonText width="w-32" height="h-5" />
                  <SkeletonText width="w-24" height="h-3" className="mt-1.5" />
                </>
              ) : (
                <>
                  <h2 className="text-base font-bold text-text-primary truncate">
                    {formatName(employee?.name || 'Unnamed Employee')}
                  </h2>
                  <p className="text-sm text-text-muted truncate">{roleName}</p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onEdit && employee && (
              <button
                type="button"
                onClick={() => onEdit(employee)}
                className="w-8 h-8 rounded-[8px] bg-primary/10 text-primary flex items-center justify-center hover:bg-primary/20 transition-colors focus:outline-none shrink-0"
                title="Edit Employee"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
              aria-label="Close"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {isLoading ? (
          <>
            <div className="bg-[#F5F5F5] rounded-[16px] p-4 sm:p-5 flex flex-col gap-3">
              <SkeletonText width="w-32" height="h-5" />
              <div className="bg-white rounded-[12px] p-4 flex flex-col gap-3.5">
                <SkeletonText width="w-full" height="h-4" />
                <SkeletonText width="w-3/4" height="h-4" />
                <SkeletonText width="w-5/6" height="h-4" />
              </div>
            </div>
            <div className="bg-[#F5F5F5] rounded-[16px] p-4 sm:p-5 flex flex-col gap-3">
              <SkeletonText width="w-40" height="h-5" />
              <div className="bg-white rounded-[12px] p-4 flex flex-col gap-3.5">
                <SkeletonText width="w-full" height="h-4" />
                <SkeletonText width="w-3/4" height="h-4" />
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Personal Details */}
            <DetailSection title="Personal Details">
              <DetailRow label="Email" value={employee?.email} />
              <DetailRow label="Phone" value={employee?.phone} />
              <DetailRow label="Position" value={employee?.position} />
              <DetailRow label="Department" value={employee?.department} />
            </DetailSection>

            <DetailSection title="Tags">
              <InlineTagSelector
                companyId={companyId}
                employeeId={employeeId}
                contactTags={Array.isArray(employee?.tags) ? employee.tags : []}
                disabled={!can(PERMISSIONS.MEMBERS.UPDATE)}
              />
            </DetailSection>

            {/* Assignment Settings */}
            <DetailSection title="Assignment Settings">
              <DetailRow
                label="Auto Assignment"
                value={
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={!!employee?.is_assignment_enabled}
                      onChange={handleToggleAutoAssign}
                      disabled={toggleAutoAssignMutation.isPending}
                    />
                    <span
                      className={
                        employee?.is_assignment_enabled
                          ? 'text-status-success'
                          : 'text-status-error'
                      }
                    >
                      {employee?.is_assignment_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                }
              />
              <DetailRow
                label="Max Daily Assignments"
                value={employee?.max_daily_assignments || 0}
              />
              <DetailRow
                label="Max New Leads"
                value={employee?.max_new_leads || 0}
              />
              <DetailRow
                label="Max Active Leads"
                value={employee?.max_active_leads || 0}
              />
              <DetailRow
                label="Max Total Open Leads"
                value={employee?.max_total_open_leads || 0}
              />
            </DetailSection>

            {/* Additional Info */}
            <DetailSection title="Additional Info">
              {employee?.metadata && employee.metadata !== '{}' && (
                <DetailRow label="Metadata" value={employee.metadata} />
              )}
              <DetailRow
                label="Created At"
                value={
                  employee?.created_at
                    ? new Date(employee.created_at).toLocaleString()
                    : '-'
                }
              />
              <DetailRow
                label="Last Assigned"
                value={
                  employee?.last_assigned_at
                    ? new Date(employee.last_assigned_at).toLocaleString()
                    : 'Never'
                }
              />
            </DetailSection>
          </>
        )}
      </div>
    </SideDrawer>
  );
}
